// Port of SuiteModel/DocumentTree.swift: an Automerge document's state as a plain value tree (maps, lists, text,
// scalars with their Automerge datatypes), read at any heads, and written into another document as the smallest
// changes that make it equal. Compaction replays history as a sequence of these states; epoch adoption rebases local
// edits with them, and absorption rebuilds an older epoch's edits in the current one.
//
// Maps that two devices each made at one key are read as one (`JoinedMap`, automerge/joined.ts), as the model reads
// them: a state written into another document — a compacted one, a merge across epochs — keeps what either device
// put in its own, in a single map. Writing into a document that holds such maps takes a key the tree lacks out of
// each, and puts what's new into the first.
//
// Writing goes through the low-level backend (as the Swift writes through automerge-swift), so the operations are the
// Swift's: deletions of keys the tree lacks first, then the tree's keys in sorted order; scalars put only when they
// differ (datatype included); objects reused when the type matches; text by `updateText`; lists rewritten whole when
// they differ.
//
// `merge`'s scratch documents have fixed actors (the Swift's are random), so a test's merge always gives the same result;
// the merge itself is the Swift's.
import * as A from '@automerge/automerge';
import { newerFirst } from '../automerge/joined.ts';
import { swiftCompare } from '../model/order.ts';

/** A JS Automerge document's backend (the low-level API). */
export type Backend = ReturnType<typeof A.getBackend>;

export type ScalarType = 'str' | 'int' | 'uint' | 'f64' | 'boolean' | 'timestamp' | 'counter' | 'bytes' | 'null';

export type DocTree =
  | { kind: 'scalar'; type: ScalarType; value: unknown }
  | { kind: 'map'; entries: RootTree }
  | { kind: 'list'; items: DocTree[] }
  | { kind: 'text'; text: string };

/** A map's entries (the root, or any map). A `Map`, so any key a document holds is safe. */
export type RootTree = Map<string, DocTree>;

const SCALARS = new Set<string>(['str', 'int', 'uint', 'f64', 'boolean', 'timestamp', 'counter', 'bytes', 'null']);

export function backendOf(source: A.Doc<unknown> | Backend): Backend {
  return typeof (source as Backend).getWithType === 'function' && !A.isAutomerge(source) ? (source as Backend) : A.getBackend(source as A.Doc<unknown>);
}

/** The root map of a document at `heads` (the current state when omitted). */
export function readTree(source: A.Doc<unknown> | Backend, heads?: string[]): RootTree {
  return readMaps(backendOf(source), ['_root'], heads);
}

/** `Document.objects` over a backend: every object of `type` at `key`, the one shown first, then newest first. */
function objectsAt(b: Backend, obj: string, key: string, type: 'map' | 'list' | 'text', heads: string[] | undefined): string[] {
  const all = (b.getAll(obj, key, heads) as unknown as [string, unknown][] | undefined) ?? [];
  const ids = all.filter((v) => v[0] === type).map((v) => v[1] as string);
  if (ids.length <= 1) return ids;
  ids.sort(newerFirst);
  const shown = b.getWithType(obj, key, heads) as [string, unknown] | null;
  const at = shown && shown[0] === type ? ids.indexOf(shown[1] as string) : -1;
  if (at > 0) ids.unshift(...ids.splice(at, 1));
  return ids;
}

/** The keys of `maps` (every map at one key), each once, in the maps' order. */
function joinedKeys(b: Backend, maps: string[], heads: string[] | undefined): string[] {
  if (maps.length === 1) return b.keys(maps[0], heads);
  const seen = new Set<string>();
  for (const map of maps) for (const key of b.keys(map, heads)) seen.add(key);
  return [...seen];
}

/** The value at `key` from the first of `maps` that holds it. */
function joinedGet(b: Backend, maps: string[], key: string, heads: string[] | undefined): [string, unknown] | null {
  for (const map of maps) {
    const v = b.getWithType(map, key, heads) as [string, unknown] | null;
    if (v) return v;
  }
  return null;
}

/** `DocumentTree.read(JoinedMap)`: the maps at one key as one map. */
function readMaps(b: Backend, maps: string[], heads: string[] | undefined): RootTree {
  const entries: RootTree = new Map();
  for (const key of joinedKeys(b, maps, heads)) {
    const full = joinedGet(b, maps, key, heads);
    const t = full && full[0] === 'map'
      ? ({ kind: 'map', entries: readMaps(b, maps.flatMap((m) => objectsAt(b, m, key, 'map', heads)), heads) } as DocTree)
      : value(b, full, heads);
    if (t) entries.set(key, t);
  }
  return entries;
}

function value(b: Backend, full: [string, unknown] | null, heads: string[] | undefined): DocTree | null {
  if (!full) return null;
  const [t, v] = full;
  if (t === 'map' || t === 'list' || t === 'text') return readObject(b, v as string, t, heads);
  if (!SCALARS.has(t)) return null; // a table or an unknown type: not something the Swift writes
  return { kind: 'scalar', type: t as ScalarType, value: v };
}

function readObject(b: Backend, obj: string, type: 'map' | 'list' | 'text', heads: string[] | undefined): DocTree {
  if (type === 'text') return { kind: 'text', text: b.text(obj, heads) };
  if (type === 'map') return { kind: 'map', entries: readMaps(b, [obj], heads) };
  const items: DocTree[] = [];
  const n = b.length(obj, heads);
  for (let i = 0; i < n; i++) {
    const t = value(b, b.getWithType(obj, i, heads) as [string, unknown] | null, heads);
    if (t) items.push(t);
  }
  return { kind: 'list', items };
}

// MARK: Equality

function sameScalarValue(type: ScalarType, a: unknown, b: unknown): boolean {
  switch (type) {
    case 'timestamp': return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
    case 'bytes': {
      if (!(a instanceof Uint8Array) || !(b instanceof Uint8Array) || a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    }
    case 'int': case 'uint': case 'f64': case 'counter':
      // uint reads back as a bigint.
      return typeof a === typeof b ? a === b || (Number.isNaN(a) && Number.isNaN(b)) : String(a) === String(b);
    default: return a === b;
  }
}

/** `DocumentTree ==`. */
export function sameNode(a: DocTree | undefined, b: DocTree | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'scalar': return a.type === (b as typeof a).type && sameScalarValue(a.type, a.value, (b as typeof a).value);
    case 'text': return a.text === (b as typeof a).text;
    case 'map': return sameTree(a.entries, (b as typeof a).entries);
    case 'list': {
      const y = (b as typeof a).items;
      return a.items.length === y.length && a.items.every((x, i) => sameNode(x, y[i]));
    }
  }
}

export function sameTree(a: RootTree, b: RootTree): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const [k, v] of a) if (!sameNode(v, b.get(k))) return false;
  return true;
}

// MARK: Writing

const objectValue = (t: DocTree): unknown => (t.kind === 'map' ? {} : t.kind === 'list' ? [] : '');

function scalarArg(t: Extract<DocTree, { kind: 'scalar' }>): unknown {
  switch (t.type) {
    case 'int': case 'uint': case 'f64': case 'counter': return Number(t.value);
    case 'timestamp': return new Date((t.value as Date).getTime());
    default: return t.value;
  }
}

/** Changes the document's root to equal `tree` (not committed). */
export function writeTree(tree: RootTree, raw: Backend): void {
  writeMap(tree, raw, ['_root']);
}

/** Makes what `readMaps` reads of `maps` (every map at one key) equal `tree`. */
function writeMap(tree: RootTree, raw: Backend, maps: string[]) {
  const first = maps[0];
  const holds = (map: string, key: string) => !!raw.getWithType(map, key);
  const dropCopies = (key: string) => {
    for (const other of maps.slice(1)) if (holds(other, key)) raw.delete(other, key);
  };
  for (const key of joinedKeys(raw, maps, undefined)) {
    if (tree.has(key)) continue;
    for (const map of maps) if (holds(map, key)) raw.delete(map, key);
  }
  for (const key of [...tree.keys()].sort(swiftCompare)) {
    const wanted = tree.get(key)!;
    const current = joinedGet(raw, maps, key, undefined);
    if (wanted.kind === 'scalar') {
      if (current && current[0] === wanted.type && sameScalarValue(wanted.type, current[1], wanted.value)) continue;
      raw.put(first, key, scalarArg(wanted) as never, wanted.type);
      dropCopies(key);
      continue;
    }
    if (wanted.kind === 'map') {
      if (current && current[0] === 'map') {
        writeMap(wanted.entries, raw, maps.flatMap((m) => objectsAt(raw, m, key, 'map', undefined)));
      } else {
        const child = raw.putObject(first, key, {} as never);
        dropCopies(key);
        writeMap(wanted.entries, raw, [child]);
      }
      continue;
    }
    let child: string;
    if (current && current[0] === wanted.kind) {
      child = current[1] as string;
    } else {
      child = raw.putObject(first, key, objectValue(wanted) as never);
      dropCopies(key);
    }
    writeObject(wanted, raw, child);
  }
}

function writeObject(tree: DocTree, raw: Backend, obj: string) {
  switch (tree.kind) {
    case 'text':
      if (raw.text(obj) !== tree.text) raw.updateText(obj, tree.text);
      return;
    case 'map':
      return writeMap(tree.entries, raw, [obj]);
    case 'list': {
      // Lists here are short (field options, attachments): rewritten whole when they differ.
      if (sameNode(readObject(raw, obj, 'list', undefined), tree)) return;
      const count = raw.length(obj);
      if (count > 0) raw.splice(obj, 0, count);
      tree.items.forEach((item, i) => {
        if (item.kind === 'scalar') raw.insert(obj, i, scalarArg(item) as never, item.type);
        else writeObject(item, raw, raw.insertObject(obj, i, objectValue(item) as never));
      });
      return;
    }
    case 'scalar':
      return;
  }
}

/** The first path where two states differ, for error messages. */
export function treeDifference(a: RootTree, b: RootTree, path = ''): string {
  for (const key of [...new Set([...a.keys(), ...b.keys()])].sort(swiftCompare)) {
    const x = a.get(key), y = b.get(key);
    if (sameNode(x, y)) continue;
    if (x?.kind === 'map' && y?.kind === 'map') return treeDifference(x.entries, y.entries, `${path}/${key}`);
    const show = (t: DocTree | undefined) => (t ? JSON.stringify(plain(t))?.slice(0, 80) : 'missing');
    return `${path}/${key}: ${show(x)} vs ${show(y)}`;
  }
  return 'no difference';
}

/** A tree as plain JSON-ish values (tests and messages). */
export function plain(t: DocTree): unknown {
  switch (t.kind) {
    case 'scalar': return t.value instanceof Date ? t.value.toISOString() : typeof t.value === 'bigint' ? Number(t.value) : t.value;
    case 'text': return t.text;
    case 'list': return t.items.map(plain);
    case 'map': return Object.fromEntries([...t.entries].map(([k, v]) => [k, plain(v)]));
  }
}

// MARK: Epoch keys

/** Root keys that say which history a document belongs to (`EpochID`, `CompactionRecord`): never merged between
 *  epochs — a merge result takes the target document's own. */
export const EPOCH_KEYS = new Set(['epoch', 'lineage', 'compaction']);

export function withoutEpochKeys(tree: RootTree): RootTree {
  return new Map([...tree].filter(([k]) => !EPOCH_KEYS.has(k)));
}

/** `tree` with the epoch keys `from` has (and none it lacks). */
export function withEpochKeysOf(tree: RootTree, from: RootTree): RootTree {
  const out = withoutEpochKeys(tree);
  for (const key of EPOCH_KEYS) if (from.has(key)) out.set(key, from.get(key)!);
  return out;
}

// MARK: Three-way merge

/** Fixed actors for the merge's scratch documents (repeatable results). */
const BASE_ACTOR = 'ba5e'.padEnd(32, '0');
const OURS_ACTOR = '0e'.padEnd(32, '1');
const THEIRS_ACTOR = '7e'.padEnd(32, '2');

/**
 * A three-way merge of document states by Automerge itself: `base` written once, copied twice (from bytes, never
 * `fork()`: the Swift's rule, whose fork panics after an empty commit then a merge), `ours` written into one and
 * `theirs` into the other, the two merged. Concurrent text edits combine character by character, as sync would.
 * Lists (attachments, field options) are settled item by item first and written by one side only.
 */
export function mergeTrees(base: RootTree, ours: RootTree, theirs: RootTree): RootTree {
  if (sameTree(ours, base)) return theirs;
  if (sameTree(theirs, base) || sameTree(theirs, ours)) return ours;
  const o = cloneTree(ours), t = cloneTree(theirs);
  settleLists(base, o, t);
  const common = A.getBackend(A.init({ actor: BASE_ACTOR }));
  writeTree(base, common);
  common.commit('base', 0);
  const bytes = common.save();
  const mine = A.getBackend(A.load(bytes, { actor: OURS_ACTOR }));
  const other = A.getBackend(A.load(bytes, { actor: THEIRS_ACTOR }));
  if (!sameTree(o, base)) {
    writeTree(o, mine);
    mine.commit('ours', 0);
  }
  if (!sameTree(t, base)) {
    writeTree(t, other);
    other.commit('theirs', 0);
  }
  other.merge(mine);
  return deduplicated(readTree(other));
}

function cloneTree(t: RootTree): RootTree {
  return new Map([...t].map(([k, v]) => [k, v.kind === 'map' ? { kind: 'map' as const, entries: cloneTree(v.entries) } : v]));
}

/** Where both sides hold a list, `ours` gets the item-by-item merge and `theirs` the base's, so only one side writes it. */
function settleLists(base: RootTree | undefined, ours: RootTree, theirs: RootTree) {
  for (const key of [...ours.keys()].filter((k) => theirs.has(k))) {
    const o = ours.get(key)!, t = theirs.get(key)!;
    if (o.kind === 'list' && t.kind === 'list') {
      const b = base?.get(key);
      ours.set(key, { kind: 'list', items: mergeList(b?.kind === 'list' ? b.items : [], o.items, t.items) });
      const was = base?.get(key);
      if (was) theirs.set(key, was);
      else theirs.delete(key);
    } else if (o.kind === 'map' && t.kind === 'map') {
      const b = base?.get(key);
      settleLists(b?.kind === 'map' ? b.entries : undefined, o.entries, t.entries);
    }
  }
}

/** An item's identity in a list: a map's `id` or `sha256`, else the item itself. */
function naturalKey(item: DocTree): string | null {
  if (item.kind !== 'map') return null;
  for (const field of ['id', 'sha256']) {
    const v = item.entries.get(field);
    if (v?.kind === 'scalar' && v.type === 'str') return `${field}:${v.value as string}`;
  }
  return null;
}

function same(a: DocTree, b: DocTree): boolean {
  const x = naturalKey(a), y = naturalKey(b);
  if (x !== null && y !== null) return x === y;
  return sameNode(a, b);
}

/** Theirs' order with ours' deletions and changes applied, then ours' additions after the item each follows. */
export function mergeList(base: DocTree[], ours: DocTree[], theirs: DocTree[]): DocTree[] {
  const result: DocTree[] = [];
  for (const t of theirs) {
    const b = base.find((x) => same(x, t)), o = ours.find((x) => same(x, t));
    if (b && !o) continue; // ours removed it
    let value = t;
    if (b && o && !sameNode(o, b)) {
      if (sameNode(t, b)) value = o;
      else if (b.kind === 'map' && o.kind === 'map' && t.kind === 'map') {
        const tm = new Map(t.entries);
        for (const [field, v] of o.entries) if (!sameNode(v, b.entries.get(field)) && sameNode(tm.get(field), b.entries.get(field))) tm.set(field, v);
        for (const field of b.entries.keys()) if (!o.entries.has(field) && sameNode(tm.get(field), b.entries.get(field))) tm.delete(field);
        value = { kind: 'map', entries: tm };
      }
    }
    if (!result.some((x) => same(x, value))) result.push(value);
  }
  ours.forEach((o, i) => {
    if (base.some((x) => same(x, o)) || result.some((x) => same(x, o))) return;
    const before = [...ours.slice(0, i)].reverse().find((p) => result.some((x) => same(x, p)));
    const at = before ? result.findIndex((x) => same(x, before)) + 1 : 0;
    result.splice(at, 0, o);
  });
  return result;
}

/** Lists with one item per natural key (the first kept). */
function deduplicated(tree: RootTree): RootTree {
  const clean = (t: DocTree): DocTree => {
    if (t.kind === 'map') return { kind: 'map', entries: deduplicated(t.entries) };
    if (t.kind !== 'list') return t;
    const seen = new Set<string>();
    const kept: DocTree[] = [];
    for (const item of t.items) {
      const key = naturalKey(item);
      if (key !== null) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      kept.push(clean(item));
    }
    return { kind: 'list', items: kept };
  };
  return new Map([...tree].map(([k, v]) => [k, clean(v)]));
}

// MARK: Building trees (tests, the CLI)

export const treeText = (text: string): DocTree => ({ kind: 'text', text });
export const treeStr = (value: string): DocTree => ({ kind: 'scalar', type: 'str', value });
export const treeMap = (entries: Record<string, DocTree>): DocTree => ({ kind: 'map', entries: new Map(Object.entries(entries)) });
export const treeList = (items: DocTree[]): DocTree => ({ kind: 'list', items });
